import type { SeverityLevel } from '@microsoft/applicationinsights-web';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;

let appInsights: ApplicationInsights | null = null;

if (connectionString) {
  try {
    const ai = new ApplicationInsights({
      config: {
        connectionString,
        disableAjaxTracking: true,
        disableFetchTracking: true,
        autoTrackPageVisitTime: false,
        enableAutoRouteTracking: false,
        disableExceptionTracking: true,
      },
    });

    ai.loadAppInsights();

    ai.addTelemetryInitializer((item) => {
      item.tags = item.tags ?? {};
      item.tags['ai.cloud.role'] = 'credit-control-frontend';
      item.tags['ai.cloud.roleInstance'] = 'browser';

      if (item.baseData) {
        item.baseData.properties = {
          ...item.baseData.properties,
          'service.name': 'credit-control-frontend',
          'pe.code': '232',
        };
      }
    });

    appInsights = ai;
  } catch (e) {
    console.error('Failed to initialize Application Insights:', e);
  }
}

export type TelemetryProperties = Record<string, string | number | boolean | undefined>;

/**
 * Records a custom event. Properties must never carry credentials, API keys, webhook secrets,
 * payment data or free text typed by a user — only identifiers, states and flags.
 */
export function trackEvent(name: string, properties?: TelemetryProperties): void {
  if (!appInsights) {
    return;
  }

  try {
    appInsights.trackEvent({ name }, properties);
  } catch (e) {
    console.error('Failed to track event in Application Insights:', e);
  }
}

export function trackError(error: Error, severityLevel?: SeverityLevel): void {
  if (!appInsights) {
    return;
  }

  try {
    appInsights.trackException({
      exception: error,
      severityLevel,
    });
  } catch (e) {
    console.error('Failed to track error in Application Insights:', e);
  }
}

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
  trackError(error);
});
